import React, {
  useState,
  useCallback,
  Fragment,
  useRef,
  useEffect,
} from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import UserService from "../services/UserService";
import { useNavigate } from "react-router-dom";
import RenderOnRole from "./RenderOnRole";
import RenderIfNotOnRole from "./RenderIfNotOnRole";
import Labels from "../labels/Labels";
import { useChosenEmployer } from "../contexts/EmployerContext";
import {
  AppBar,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ChangeCircle } from "@mui/icons-material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";

export default function RellevateMenu() {
  const [RellevateLogo, setRellevateLogo] = React.useState(null);
  const [MiddleLogo, setMiddleLogo] = React.useState(null);
  const getInitials = useCallback(async (subdomain) => {
    try {
      const extension = subdomain ? `.${subdomain}` : "";
      const smallLogo = await import(`../company-logo${extension}.svg`);
      setRellevateLogo(smallLogo.default);
    } catch (error) {
      console.error(error);
      getInitials("");
    }
    try {
      const extension = subdomain ? `.${subdomain}` : "";
      const smallMiddleLogo = await import(
        `../company-middle-logo${extension}.svg`
      );
      setMiddleLogo(smallMiddleLogo.default);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    getInitials(window.subdomain);
  }, [getInitials]);

  const {
    employerData,
    hasMultipleEmployers,
    isLoadingEmployer,
    onSwitchEmployer,
  } = useChosenEmployer();
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState([]);
  const [nestedMenu, setNestedMenu] = useState({ anchorEl: null, items: [] });
  const handleClick = (event) => {
    setAnchorEl(() => ({
      [event.currentTarget.innerText.replace(/\n/g, "")]: event.currentTarget,
    }));
  };

  const handleClose = () => {
    setAnchorEl([]);
    setNestedMenu({ anchorEl: null, items: [] });
  };

  const handleMenuItemClick = (route) => {
    navigate(route);
    handleClose();
  };

  const openNestedMenu = (event, items) => {
    setNestedMenu({ anchorEl: event.currentTarget, items });
  };

  const APPROVE_BUNDLE_ROLE = "pc-approve-bundle";

  const menuNavRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = menuNavRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.offsetWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = menuNavRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollNav = (dir) => {
    const el = menuNavRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 160, behavior: "smooth" });
  };

  const menuItems = [
    {
      label: Labels.getLabel("menu.cards", "Cards"),
      subMenus: [
        {
          role: ["pc-query-available-paycards"],
          label: Labels.getLabel("menu.queryAvailable", "Query available"),
          route: "/queryAvailablePaycards",
        },
        {
          role: ["pc-query-assigned-paycards"],
          label: Labels.getLabel("menu.queryAssigned", "Query assigned"),
          route: "/queryAssignedPaycards",
        },
        {
          role: ["pc-inventory"],
          label: "Inventory",
          children: [
            { label: "Receive Cards", route: "/cardInventory/reception" },
            {
              label: "Enable Cards for Sale",
              route: "/cardInventory/assign-enablement",
            },
            { label: "Query Operations", route: "/cardInventory/operations" },
          ],
        },
        {
          role: [
            "pc-employer-super-admin",
            APPROVE_BUNDLE_ROLE,
            "pc-query-bundle",
          ],
          notInRole: ["pf-only-disburse", "pc-hide-request-new-card-orders"],

          label: Labels.getLabel("menu.requestNewOrder", "Request (New Order)"),
          route: "/requestPaycards",
        },
        {
          role: ["pc-program-super-admin", "compliance-mgr"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.uploadCipFile", "Upload CIP File"),
          route: "/cip",
        },
        {
          role: ["pc-program-super-admin", "compliance-mgr"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.uploadEwaCollections",
            "Upload EWA Collection Attempts File"
          ),
          route: "/ewa/collectionAttempts",
        },
        {
          role: ["pc-on-the-fly"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.assignCardOnTheFly",
            "Assign Card On-The-Fly (No Inventory)"
          ),
          route: "/assignCardOnTheFly",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.locations", "Locations"),
      subMenus: [
        {
          role: ["pc-dashboard-locations"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.viewDashboard", "View Dashboard"),
          route: "/dashboardLocations",
        },
        {
          role: ["pc-query-locations"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.queryLocations", "Query Locations"),
          route: "/locations",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.payments", "Payments"),
      subMenus: [
        {
          role: ["bl-crud"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.manageBalances", "Manage balances"),
          route: "/manageBalance",
        },
        {
          role: ["pf-viewer"],
          notInRole: ["pf-multibalance", "pf-only-disburse"],
          label: Labels.getLabel("menu.companyBalance", "Company Balance"),
          route: "/balance",
        },
        {
          role: ["pf-multibalance"],

          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.companyBalances", "Company Balances"),
          route: "/multiBalance",
        },
        {
          role: ["bl-transfer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.interCompanyTransfers",
            "Inter Company Transfers"
          ),
          route: "/transfers",
        },
        {
          role: ["transfers-viewer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.viewTransfers", "View Transfers"),
          route: "/viewTransfers",
        },
        {
          role: ["pf-viewer"],

          label: Labels.getLabel("menu.paymentRequests", "Payment Requests"),
          route: "/payments/summary",
        },
        {
          role: ["compliance-mgr"],
          label: "Proof of Funds",
          route: "/proofOfFunds",
        },
        {
          role: ["pf-viewer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.queryAll", "Query All"),
          route: "/payments",
        },
        {
          role: ["pf-viewer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.queryPending", "Query Pending"),
          route: "/pendingPayments",
        },
        {
          role: ["is-whitelist"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.whitelist", "Whitelist"),
          route: "/whitelist",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.membersWorkers", "Members/ Workers"),
      subMenus: [
        {
          role: ["employee-viewer"],

          label: Labels.getLabel("menu.queryMembers", "Query Members"),
          route: "/companyMembers",
        },
        {
          role: ["pad-viewer","compliance-viewer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.queryMemberInfoInternal",
            "Query Member Info (Internal)"
          ),
          route: "/members",
        },
        {
          role: ["pad-viewer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.queryTimeAttendance",
            "Query Time and Attendance"
          ),
          route: "/timeAttendance",
        },
        {
          role: ["pad-viewer"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.queryMissingTimeAttendance",
            "Query Missing Time and Attendance"
          ),
          route: "/timeAttendance/missing",
        },
        {
          role: ["pad-deduction"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.payAnyDayDeduction",
            "Pay Any-Day - Payroll Deduction"
          ),
          route: "/pad/deduction",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.onboarding", "Onboarding"),
      subMenus: [
        {
          role: ["compliance-mgr"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.queryPendingOnboardings",
            "Query Pending Onboardings (Review)"
          ),
          route: "/onboardings",
        },
        {
          role: ["pc-program-removal"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.queryStartedOnboardings",
            "Query Started Onboardings"
          ),
          route: "/onboardings/started",
        },
        {
          role: ["onboarding-manage"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.queryStartedOnboardingsRds",
            "Query Started Onboardings (RDS)"
          ),
          route: "/onboardings/started/rds",
        },
        {
          role: ["pc-program-super-admin"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.bulkOnboardings", "Bulk Onboardings"),
          route: "/onboardings/bulk",
        },
        {
          role: ["pc-program-super-admin", "census-updater"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.preauthorizedPadStandalone",
            "Census Update for Pay Any-Day (Payroll Deduction)"
          ),
          route: "/onboardings/preauthorizeStandalone",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.configure", "Administration"),
      subMenus: [
        {
          role: ["guardlocker-manager"],
          label: Labels.getLabel("menu.guardLockerManager", "GuardLocker Manager"),
          route: "/guardlocker",
        },
        {
          role: ["config-employer-attr"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.manageEmployerConfig",
            "Manage Companies"
          ),
          route: "/employer",
        },
        {
          role: ["config-employer-attr"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.companySettings", "Company Settings"),
          route: "/employerAttr",
        },

        {
          role: ["pd-view"],
          //notInRole: ["pf-only-disburse"],
          label: Labels.getLabel(
            "menu.paymentDescriptors",
            "Payment Descriptors"
          ),
          route: "/paymentDescriptors",
        },

        {
          role: ["pc-program-super-admin"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.spendRules", "Spend Rules"),
          route: "/spendRules",
        },

        {
          role: ["pc-program-super-admin"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.lookup", "Lookup"),
          route: "/lookup",
        },

        {
          role: ["pc-program-super-admin"],
          label: Labels.getLabel("menu.knowledgeBase", "Knowledge Base"),
          route: "/knowledgeBase",
        },

        {
          role: ["config-campaigns"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.campaigns", "Campaigns"),
          route: "/campaignsConfig",
        },
        {
          role: ["config-admin-fees"],
          label: Labels.getLabel(
            "menu.administrativeFees",
            "Administrative Fees"
          ),
          route: "/administrativeFeesConfig",
        },
        {
          role: ["config-users", "pc-program-super-admin"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.manageUsers", "Manage Users"),
          route: "/users",
        },
        {
          role: ["ap-view", "ap-decide"],
          label: "Approvals",
          route: "/approvals",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.manageGroups", "Manage Groups"),
      subMenus: [
        {
          role: ["group-crud"],

          label: Labels.getLabel("menu.manageCompanyGroups", "Company Groups"),
          route: "/manageGroup",
        },
        {
          role: ["group-member"],

          label: Labels.getLabel("menu.manageGroupMembers", "Group Members"),
          route: "/manageGroup",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.reports", "Reports"),
      subMenus: [
        {
          role: ["pc-reports"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.cardsDashboard", "Cards Dashboard"),
          route: "/cardsDashboard",
        },
        {
          role: ["pc-flash-report"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.flashReports", "Flash Reports"),
          route: "/flashReports",
        },
      ],
    },
    {
      label: Labels.getLabel(
        "menu.organizationalUnits",
        "Organizational Units"
      ),
      subMenus: [
        {
          role: ["config-subdepartment"],
          notInRole: ["pf-only-disburse", "subdepartment-hide-view"],
          label: Labels.getLabel("menu.configureOrgUnits", "Manage"),
          route: "/manageOrganizationalUnit",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.misc", "Misc"),
      subMenus: [
        {
          role: ["pc-misc-upload"],
          notInRole: ["pf-only-disburse"],
          label: Labels.getLabel("menu.uploadFile", "Upload File"),
          route: "/uploadFile",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.supportChat", "Support Chat"),
      subMenus: [
        {
          role: ["chat-support"],
          label: Labels.getLabel(
            "menu.supportChatDashboard",
            "Agent Dashboard"
          ),
          route: "/supportChat",
        },
        {
          role: ["chat-support"],
          label: Labels.getLabel(
            "menu.supportChatAllConversations",
            "All Conversations"
          ),
          route: "/supportChat/all",
        },
      ],
    },
    {
      label: Labels.getLabel("menu.scheduler", "Scheduler"),
      subMenus: [
        {
          role: ["scheduler-admin", "pc-program-super-admin"],
          label: Labels.getLabel(
            "menu.schedulerTemplates",
            "Schedule Templates"
          ),
          route: "/communication/scheduler/templates",
        },
        {
          role: ["scheduler-admin", "pc-program-super-admin"],
          label: Labels.getLabel(
            "menu.schedulerCampaigns",
            "Onboarding Campaigns"
          ),
          route: "/communication/scheduler/campaigns",
        },
        {
          role: ["scheduler-admin", "pc-program-super-admin"],
          label: Labels.getLabel(
            "menu.schedulerInstances",
            "Communication Schedule Instances"
          ),
          route: "/communication/scheduler/instances",
        },
        {
          role: ["pc-program-super-admin"],
          label: Labels.getLabel(
            "menu.notificationTemplates",
            "Notification Templates"
          ),
          route: "/communication/notification-templates",
        },
      ],
    },
  ];

  const hasVisibleSubMenus = (item) => {
    return item.subMenus.some((subMenu) => {
      // Check if user has required role
      const hasRole = !subMenu.role || UserService.hasRole(subMenu.role);

      // Check if user is NOT in excluded roles
      const notInExcludedRole =
        !subMenu.notInRole || !UserService.hasRole(subMenu.notInRole);

      return hasRole && notInExcludedRole;
    });
  };

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          boxShadow: "0 10px 8px 0 rgba(0,0,0,.15)",
          minHeight: MiddleLogo ? "120px" : "100px",
        }}
      >
        <Toolbar
          sx={{
            minHeight: MiddleLogo ? "120px !important" : "100px !important",
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "end",
            position: "relative",
            paddingBottom: "2px",
            gap: 2,
          }}
        >
          {/* Middle Logo - Centered at top */}
          {MiddleLogo && (
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2,
              }}
            >
              <img
                src={MiddleLogo}
                alt={Labels.getLabel("menu.rellevateLogo", "Rellevate Logo")}
                loading="lazy"
                style={{
                  maxWidth: "230px",
                  maxHeight: "70px",
                  height: "70px",
                }}
              />
            </Box>
          )}
          {/* Left logo - in first grid column, centered vertically */}
          <Box sx={{ alignSelf: "center", minWidth: "220px" }}>
            {RellevateLogo ? (
              <img
                src={RellevateLogo}
                alt={Labels.getLabel("menu.rellevateLogo", "Rellevate Logo")}
                style={{
                  width: "100%",
                  maxWidth: "220px",
                  height: "auto",
                  display: "block",
                }}
              />
            ) : (
              <Box sx={{ width: "220px", height: "40px" }} />
            )}
          </Box>
          {/* Menu options - in second grid column, at bottom */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {canScrollLeft && (
              <IconButton
                size="small"
                onClick={() => scrollNav(-1)}
                sx={{ flexShrink: 0, color: "text.secondary" }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            <Box
              ref={menuNavRef}
              sx={{
                display: "flex",
                alignItems: "center",
                overflowX: "hidden",
                flex: 1,
                minWidth: 0,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                gap: 0.5,
                "& > *": { flexShrink: 0 },
                "& .MuiButton-root": { whiteSpace: "nowrap" },
              }}
            >
              <Button onClick={() => handleMenuItemClick("/")}>
                {Labels.getLabel("menu.home", "Home")}
              </Button>

              {menuItems.map((item, index) => {
                // Special case for Organizational Units menu item
                if (
                  item.label ===
                  Labels.getLabel(
                    "menu.organizationalUnits",
                    "Organizational Units"
                  )
                ) {
                  // Only show menu if it has at least one visible submenu
                  if (!hasVisibleSubMenus(item)) {
                    return null;
                  }

                  return (
                    <RenderIfNotOnRole
                      key={"renderIfNotOnRole." + index}
                      roles={["internal"]}
                    >
                      <React.Fragment key={"fragment." + index}>
                        <Button
                          key={"button." + index}
                          endIcon={<ArrowDropDownIcon />}
                          onClick={handleClick}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          key={"menu." + index}
                          anchorEl={anchorEl[item.label.toUpperCase()]}
                          open={Boolean(anchorEl[item.label.toUpperCase()])}
                          onClose={handleClose}
                        >
                          {item.subMenus.map((subMenu, index) => {
                            return (
                              <RenderOnRole
                                key={"renderOnRole." + index}
                                roles={subMenu.role}
                              >
                                <MenuItem
                                  key={"menuItem." + index}
                                  onClick={() =>
                                    handleMenuItemClick(subMenu.route)
                                  }
                                >
                                  {subMenu.label}
                                </MenuItem>
                              </RenderOnRole>
                            );
                          })}
                        </Menu>
                      </React.Fragment>
                    </RenderIfNotOnRole>
                  );
                }

                // Regular menu items
                // Only show menu if it has at least one visible submenu
                if (!hasVisibleSubMenus(item)) {
                  return null;
                }

                return (
                  <React.Fragment key={"fragment." + index}>
                    <Button
                      key={"button." + index}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleClick}
                    >
                      {item.label}
                    </Button>
                    <Menu
                      key={"menu." + index}
                      anchorEl={anchorEl[item.label.toUpperCase()]}
                      open={Boolean(anchorEl[item.label.toUpperCase()])}
                      onClose={handleClose}
                    >
                      {item.subMenus.map((subMenu, index) => {
                        // Case 1: Both role AND notInRole specified

                        if (subMenu.children !== undefined) {
                          return (
                            <RenderOnRole
                              key={"renderOnRole." + index}
                              roles={subMenu.role}
                            >
                              <MenuItem
                                key={"menuItem." + index}
                                onMouseEnter={(event) =>
                                  openNestedMenu(event, subMenu.children)
                                }
                                onClick={(event) =>
                                  openNestedMenu(event, subMenu.children)
                                }
                              >
                                {subMenu.label}
                                <ChevronRightIcon sx={{ ml: "auto" }} />
                              </MenuItem>
                            </RenderOnRole>
                          );
                        }

                        if (
                          subMenu.role !== undefined &&
                          subMenu.notInRole !== undefined
                        ) {
                          return (
                            <RenderOnRole
                              key={"renderOnRole." + index}
                              roles={subMenu.role}
                            >
                              <RenderIfNotOnRole
                                key={"renderIfNotOnRole." + index}
                                roles={subMenu.notInRole}
                              >
                                <MenuItem
                                  key={"menuItem." + index}
                                  onClick={() =>
                                    handleMenuItemClick(subMenu.route)
                                  }
                                >
                                  {subMenu.label}
                                </MenuItem>
                              </RenderIfNotOnRole>
                            </RenderOnRole>
                          );
                        }

                        // Case 2: Only notInRole specified
                        if (subMenu.notInRole !== undefined) {
                          return (
                            <RenderIfNotOnRole
                              key={"renderIfNotOnRole." + index}
                              roles={subMenu.notInRole}
                            >
                              <MenuItem
                                key={"menuItem." + index}
                                onClick={() =>
                                  handleMenuItemClick(subMenu.route)
                                }
                              >
                                {subMenu.label}
                              </MenuItem>
                            </RenderIfNotOnRole>
                          );
                        }

                        // Case 3: Only role specified (or neither)
                        return (
                          <RenderOnRole
                            key={"renderOnRole." + index}
                            roles={subMenu.role}
                          >
                            <MenuItem
                              key={"menuItem." + index}
                              onClick={() => handleMenuItemClick(subMenu.route)}
                            >
                              {subMenu.label}
                            </MenuItem>
                          </RenderOnRole>
                        );
                      })}
                    </Menu>
                    <Menu
                      anchorEl={nestedMenu.anchorEl}
                      open={Boolean(nestedMenu.anchorEl)}
                      onClose={() =>
                        setNestedMenu({ anchorEl: null, items: [] })
                      }
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                      {nestedMenu.items.map((child) => (
                        <MenuItem
                          key={child.route}
                          onClick={() => handleMenuItemClick(child.route)}
                        >
                          {child.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                );
              })}
            </Box>{" "}
            {/* /inner scrollable Box */}
            {canScrollRight && (
              <IconButton
                size="small"
                onClick={() => scrollNav(1)}
                sx={{ flexShrink: 0, color: "text.secondary" }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>{" "}
          {/* /outer overflow Box */}
          {/* User info - in third grid column, at bottom */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isLoadingEmployer ? (
                <Typography sx={{ color: "grey", fontSize: "16px" }}>
                  {Labels.getLabel(
                    "menu.loadingOrganization",
                    "Loading organization..."
                  )}
                </Typography>
              ) : hasMultipleEmployers && onSwitchEmployer ? (
                <Button
                  size="small"
                  color="primary"
                  onClick={onSwitchEmployer}
                  sx={{
                    fontSize: "12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textTransform: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: 500,
                        textAlign: "left",
                      }}
                    >
                      {employerData?.name ||
                        Labels.getLabel("menu.organization", "Organization")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "9px",
                          color: theme.palette.primary.secondary,
                          fontWeight: "bold",
                        }}
                      >
                        {Labels.getLabel("menu.switchOrganization", "SWITCH")}
                      </Typography>
                      <SwapHorizIcon sx={{ fontSize: "14px" }} />
                    </Box>
                  </Box>
                </Button>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    marginRight: "1rem",
                  }}
                >
                  <Typography
                    variant="h8"
                    component="div"
                    sx={{
                      color: "grey",
                      fontSize: "18px",
                      lineHeight: 1.2,
                    }}
                  >
                    {employerData?.name ||
                      Labels.getLabel("menu.organization", "Organization")}
                  </Typography>
                </Box>
              )}
              <Button
                color="secondary"
                variant="contained"
                sx={{ fontSize: "15px", marginTop: "5px" }}
                onClick={() => {
                  handleMenuItemClick("/");
                  UserService.doLogout();
                }}
              >
                {Labels.getLabel("menu.signOut", "SIGN OUT")}
              </Button>
            </Box>
            <Typography
              variant="h8"
              component="div"
              sx={{
                color: "grey",
                marginRight: "1rem",
              }}
            >
              {Labels.getLabel("menu.user", "User")}:{" "}
              {UserService.getUsername() ||
                Labels.getLabel("menu.guest", "Guest")}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}
